/**
 * Purpose: Provides web push utility functions that can be used outside of React components
 *
 * This module exports non-hook based functions for managing web push notifications
 * that can be safely used in middleware or other non-React contexts.
 *
 * Key features:
 * - Browser support detection
 * - User-specific storage management
 * - PushSubscription creation and deletion
 * - Socket.io integration for server communication
 * - Error handling with i18n translation support
 * - Automatic subscription restoration
 * - Timeout management for service worker and public key retrieval
 */

import { Socket } from "socket.io-client";
import i18next from "i18next";
import { SocketEventTypes } from "@/socketIO/types";
import { safeStorage } from "@/utils/safeStorage";

// Constants
export const SERVICE_WORKER_TIMEOUT_MS = 3000;
export const PUBLIC_KEY_TIMEOUT_MS = 5000;

// Error types
export enum WebPushErrorType {
  SOCKET_DISCONNECTED = "socket_disconnected",
  MISSING_DEPENDENCIES = "missing_dependencies",
  SERVICE_WORKER_TIMEOUT = "service_worker_timeout",
  PUBLIC_KEY_TIMEOUT = "public_key_timeout",
  PERMISSION_DENIED = "permission_denied",
  NOT_SUPPORTED = "not_supported",
}

// Interface for custom web push error data
export interface WebPushErrorData {
  type: WebPushErrorType;
  message: string;
  name: string;
}

/**
 * Create a web push error with the specified type and message
 * @param type The type of web push error
 * @param message The error message
 * @returns Error object with WebPushErrorData
 */
export const createWebPushError = (
  type: WebPushErrorType,
  message: string,
): Error & WebPushErrorData => {
  const error = new Error(message) as Error & WebPushErrorData;
  error.type = type;
  error.name = "WebPushError";
  return error;
};

/**
 * Check if an unknown error is a WebPushErrorData
 * @param error Unknown error to check
 * @returns Type guard indicating if the error is a WebPushErrorData
 */
export const isWebPushError = (error: unknown): error is WebPushErrorData => {
  return (
    error !== null &&
    typeof error === "object" &&
    "type" in error &&
    "name" in error &&
    (error as { name: string }).name === "WebPushError"
  );
};

/**
 * Get translated error message based on error type and message
 * @param error WebPushErrorData containing error type and message
 * @returns Translated error message using i18next
 */
export const getWebPushTranslatedMessage = (
  error: WebPushErrorData,
): string => {
  const t = i18next.t.bind(i18next);

  switch (error.type) {
    case WebPushErrorType.SOCKET_DISCONNECTED:
      return t("webPush.errors.socketNotConnected");
    case WebPushErrorType.MISSING_DEPENDENCIES:
      return t("webPush.errors.missingDependencies", {
        deps: error.message.replace("Missing dependencies: ", ""),
      });
    case WebPushErrorType.SERVICE_WORKER_TIMEOUT:
      return t("webPush.errors.serviceWorkerTimeout", {
        seconds: parseInt(error.message.match(/\d+/)?.[0] || "3"),
      });
    case WebPushErrorType.PUBLIC_KEY_TIMEOUT:
      return t("webPush.errors.publicKeyTimeout");
    case WebPushErrorType.PERMISSION_DENIED:
      return t("webPush.errors.notificationPermissionDenied");
    case WebPushErrorType.NOT_SUPPORTED:
      return t("webPush.errors.notSupported", {
        defaultValue: "Web Push not supported in this browser",
      });
    default:
      return error.message;
  }
};

/**
 * Create a new error with translated message
 * @param error WebPushErrorData to translate
 * @returns Error with translated message
 */
export const toWebPushTranslatedError = (error: WebPushErrorData): Error => {
  return new Error(getWebPushTranslatedMessage(error));
};

/**
 * Check if web push is supported by the browser
 */
export function isWebPushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window;
}

/**
 * Generate web push subscription storage keys
 * @param username Username
 * @returns Storage keys for web push subscription
 */
export function getWebPushStorageKeys(username: string) {
  const baseKey = `${username}-webPush`;
  return {
    userPreference: `${baseKey}-userPreference`, // Stores if user subscribed (toggled on/off by user)
    subscriptionData: `subscriptionData`, // Stores the actual subscription data
    activeStatus: `${baseKey}-activeStatus`, // Stores if currently subscribed
  };
}

/**
 * Check if user is subscribed to web push based on user preference
 */
export function isWebPushSubscribed(username: string): boolean {
  const keys = getWebPushStorageKeys(username);
  return safeStorage.get(keys.userPreference) === "true";
}

/**
 * Check if user's subscription is currently active
 */
export function isWebPushActive(username: string): boolean {
  const keys = getWebPushStorageKeys(username);
  return safeStorage.get(keys.activeStatus) === "true";
}

/**
 * Get stored subscription data if available
 * @param username Username for storage key lookup
 * @returns Stored PushSubscription or null if not found
 */
export function getStoredSubscription(
  username: string,
): PushSubscription | null {
  const keys = getWebPushStorageKeys(username);
  const data = safeStorage.get(keys.subscriptionData);
  if (!data) {
    return null;
  }
  try {
    return JSON.parse(data) as PushSubscription;
  } catch {
    // Corrupted localStorage — drop it and treat as no stored subscription.
    safeStorage.remove(keys.subscriptionData);
    return null;
  }
}

/**
 * Convert a URL-safe base64 string to a Uint8Array
 * Used for converting VAPID public keys to the format required by PushManager
 * @param base64String Base64 string to convert
 * @returns Uint8Array representation of the base64 string
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Resolve the active service worker registration, with a timeout so a stuck
 * `serviceWorker.ready` promise cannot hang subscription restoration.
 * Returns undefined when no service worker is available in time.
 */
export async function getReadyRegistration(
  timeoutMs = SERVICE_WORKER_TIMEOUT_MS,
): Promise<ServiceWorkerRegistration | undefined> {
  if (!("serviceWorker" in navigator)) {
    return undefined;
  }
  try {
    return (await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<undefined>((resolve) =>
        setTimeout(() => resolve(undefined), timeoutMs),
      ),
    ])) as ServiceWorkerRegistration | undefined;
  } catch {
    return undefined;
  }
}

/**
 * Creates a subscription with the server's public key
 * @param key VAPID public key
 * @param socket Socket.io connection
 * @param username Username for storage key
 * @param serviceWorkerTimeoutMs Timeout in milliseconds for the service worker to be ready
 * @returns Promise with the subscription
 */
export async function generateWebPushSubscription(
  key: string,
  socket: Socket,
  username: string,
  serviceWorkerTimeoutMs = SERVICE_WORKER_TIMEOUT_MS,
): Promise<PushSubscription> {
  if (!isWebPushSupported()) {
    throw createWebPushError(
      WebPushErrorType.NOT_SUPPORTED,
      "Web Push not supported in this browser",
    );
  }

  if (!socket) {
    throw createWebPushError(
      WebPushErrorType.SOCKET_DISCONNECTED,
      "Socket not connected",
    );
  }

  if (!key) {
    throw createWebPushError(
      WebPushErrorType.MISSING_DEPENDENCIES,
      "Missing dependencies: publicKey",
    );
  }

  try {
    const registration = await getReadyRegistration(serviceWorkerTimeoutMs);
    if (!registration) {
      throw createWebPushError(
        WebPushErrorType.SERVICE_WORKER_TIMEOUT,
        `Service worker not ready after ${serviceWorkerTimeoutMs / 1000} seconds`,
      );
    }

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key),
    });

    socket.emit(SocketEventTypes.ADD_SUBSCRIPTION, subscription);

    // Store all three states in localStorage
    const keys = getWebPushStorageKeys(username);
    // 1. User preference - user has opted in
    safeStorage.set(keys.userPreference, "true");
    // 2. Subscription data - store the subscription object
    safeStorage.set(keys.subscriptionData, JSON.stringify(subscription));
    // 3. Active status - subscription is active
    safeStorage.set(keys.activeStatus, "true");
    return subscription;
  } catch (error: unknown) {
    // Handle error based on type
    if (error instanceof Error && "type" in error) {
      const webPushError = error as WebPushErrorData;
      // eslint-disable-next-line no-console
      console.error(
        `WebPushError (${webPushError.type}):`,
        webPushError.message,
      );
    } else {
      // eslint-disable-next-line no-console
      console.error("Error checking subscription:", error);
    }
    throw error;
  }
}

/**
 * Delete a subscription (user-initiated - when the user toggles off).
 *
 * Unlike logout, this fully unsubscribes at the browser level. That matters:
 * pushManager.subscribe() with the same VAPID key returns the *existing*
 * subscription unchanged, so without a real unsubscribe() here, toggling off
 * and back on would re-register the same (possibly dead) endpoint and could
 * never mint a fresh token. Unsubscribing lets the next subscribe() create a
 * brand-new subscription — the user's recovery path for a stale token.
 *
 * @param socket Socket.io connection
 * @param username Username for storage key
 */
export async function deleteWebPushSubscription(
  socket: Socket,
  username: string,
): Promise<void> {
  if (!socket) {
    throw createWebPushError(
      WebPushErrorType.SOCKET_DISCONNECTED,
      "Socket not connected",
    );
  }

  const keys = getWebPushStorageKeys(username);

  // Resolve the live browser subscription so the server drops the correct
  // endpoint even when the stored payload is missing or stale (e.g. after the
  // push service rotated the endpoint).
  let existing: PushSubscription | null = null;
  try {
    const registration = await getReadyRegistration();
    existing = (await registration?.pushManager.getSubscription()) ?? null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to read push subscription for deletion:", error);
  }

  // Tell the server to drop both the stored endpoint and the live one
  // (deduped) so the right record is removed regardless of staleness.
  const seenEndpoints = new Set<string>();
  const emitDelete = (payload: { endpoint?: string } | null): void => {
    if (!payload?.endpoint || seenEndpoints.has(payload.endpoint)) {
      return;
    }
    seenEndpoints.add(payload.endpoint);
    socket.emit(SocketEventTypes.DELETE_SUBSCRIPTION, payload);
  };

  // Unsubscribe at the browser level first. If this rejects we let it
  // propagate WITHOUT telling the server or clearing local state, so the UI
  // can't show "off" while the browser subscription is still live — which
  // would also defeat the "toggle off/on mints a fresh token" recovery path.
  if (existing) {
    const removed = await existing.unsubscribe();
    if (!removed) {
      // `false` typically means the subscription was already gone; the desired
      // end state (no live subscription) holds, so it's safe to proceed.
      // eslint-disable-next-line no-console
      console.warn(
        "[webpush] unsubscribe() returned false; subscription may already be removed",
      );
    }
  }

  // Browser subscription is gone — drop it on the server and clear local state.
  emitDelete(getStoredSubscription(username));
  emitDelete(existing);

  // Clear the stored subscription too so a re-subscribe starts from a clean slate.
  safeStorage.remove(keys.subscriptionData);
  safeStorage.set(keys.userPreference, "false"); // 1. User preference - user has opted out
  safeStorage.set(keys.activeStatus, "false"); // 3. Active status - subscription is not active
}

/**
 * Unsubscribe from web push during logout
 * This preserves the user's preference while marking the subscription as inactive
 * Returns a promise that resolves when the server acknowledges the unsubscription
 * or after a timeout to prevent blocking logout indefinitely
 * @param socket Socket.io connection
 * @param username Username for storage key
 */
export async function unsubscribeOnLogout(
  socket: Socket,
  username: string,
): Promise<void> {
  const UNSUBSCRIBE_TIMEOUT_MS = 2000;

  // Update localStorage states immediately - ONLY change the active status, preserve user preference
  const keys = getWebPushStorageKeys(username);
  safeStorage.set(keys.activeStatus, "false");

  if (!socket?.connected) {
    return;
  }

  // Get current subscription data to send with the event
  const subscriptionData = safeStorage.get(keys.subscriptionData);
  if (!subscriptionData) {
    return;
  }

  // Wait for the server to acknowledge the delete, with a timeout.
  // NOTE: The ack callback (3rd arg) only fires if the server handler calls its ack function.
  // If the server doesn't support acks, the timeout ensures we don't block logout indefinitely.
  try {
    const parsed = JSON.parse(subscriptionData);
    await new Promise<void>((resolve) => {
      const timeout = setTimeout(resolve, UNSUBSCRIBE_TIMEOUT_MS);
      socket.emit(SocketEventTypes.DELETE_SUBSCRIPTION, parsed, () => {
        clearTimeout(timeout);
        resolve();
      });
    });
  } catch {
    // Corrupted localStorage data — clear it and return
    safeStorage.remove(keys.subscriptionData);
  }
}

/**
 * Wait for the public key with a timeout
 * @param socket Socket.io connection
 * @param publicKeyTimeoutMs Timeout in milliseconds
 * @returns Promise with the public key
 */
export function waitForWebPushPublicKey(
  socket: Socket,
  publicKeyTimeoutMs = PUBLIC_KEY_TIMEOUT_MS,
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    if (!socket) {
      reject(
        createWebPushError(
          WebPushErrorType.SOCKET_DISCONNECTED,
          "Socket not connected",
        ),
      );
      return;
    }

    const timeoutId: NodeJS.Timeout = setTimeout(() => {
      reject(
        createWebPushError(
          WebPushErrorType.PUBLIC_KEY_TIMEOUT,
          "Timeout waiting for public key",
        ),
      );
    }, publicKeyTimeoutMs);

    const keyListener = (key: string) => {
      clearTimeout(timeoutId);
      resolve(key);
    };

    socket.emit(SocketEventTypes.GET_PUSH_PUBKEY, keyListener);
  });
}

/**
 * Check previous subscription and resubscribe if necessary
 * If user has previously opted in but subscription is not active (e.g. after logout),
 * this will automatically resubscribe them
 *
 * @param socket Socket.io connection
 * @param username Username for storage key
 * @param publicKeyTimeoutMs Timeout in milliseconds for waiting for the public key
 * @returns Promise that resolves when the check is complete
 */
export async function restoreWebPushSubscriptionIfNeeded(
  socket: Socket,
  username: string,
  publicKeyTimeoutMs = PUBLIC_KEY_TIMEOUT_MS,
): Promise<void> {
  try {
    if (!socket || !username) {
      throw createWebPushError(
        WebPushErrorType.MISSING_DEPENDENCIES,
        `Missing dependencies: ${!socket ? "socket" : ""} ${!username ? "username" : ""}`,
      );
    }

    if (!isWebPushSupported()) {
      return; // Silently fail if not supported
    }

    // The user has not opted in — nothing to restore.
    if (!isWebPushSubscribed(username)) {
      return;
    }

    // Verify the *actual* browser subscription instead of trusting our
    // localStorage "active" flag. iOS can silently drop a PWA's push
    // subscription (after an OS update, app reinstall, or expiry). When that
    // happens the stored flag stays "true", so the old guard
    // (`userPreference && !isActive`) never re-subscribed and the user was
    // left with no web push at all — even though the app still believed it was
    // subscribed. Reconciling against pushManager.getSubscription() recovers
    // from this and from server-side subscription loss.
    const registration = await getReadyRegistration();
    if (!registration) {
      // Service worker not ready yet — try again on the next connect.
      return;
    }

    const existing = await registration.pushManager.getSubscription();
    const keys = getWebPushStorageKeys(username);

    if (existing) {
      // A real subscription exists. Ensure the server has *this* endpoint:
      // re-register when it has rotated, or when we have not confirmed it
      // active with the server yet (e.g. login after logout).
      const stored = getStoredSubscription(username);
      const endpointChanged = stored?.endpoint !== existing.endpoint;

      if (endpointChanged || !isWebPushActive(username)) {
        socket.emit(SocketEventTypes.ADD_SUBSCRIPTION, existing);
        safeStorage.set(keys.subscriptionData, JSON.stringify(existing));
        safeStorage.set(keys.activeStatus, "true");
      }
      return;
    }

    // No real subscription exists — (re)create one and register it with the
    // server. generateWebPushSubscription persists all three storage states.
    const key = await waitForWebPushPublicKey(socket, publicKeyTimeoutMs);
    await generateWebPushSubscription(key, socket, username);
  } catch (error: unknown) {
    // If we have a WebPushError, translate it
    if (error && typeof error === "object" && "type" in error) {
      const webPushError = error as WebPushErrorData;
      // eslint-disable-next-line no-console
      console.error(
        "Failed to restore web push subscription:",
        getWebPushTranslatedMessage(webPushError),
      );
      throw toWebPushTranslatedError(webPushError);
    }
    // eslint-disable-next-line no-console
    console.error("Failed to restore web push subscription:", error);
    throw error;
  }
}
